// assets/rs-build-your-box.js
import { Component } from '@theme/component';

/**
 * Build Your Box component for the Raw Food Box PDP.
 * Manages protein/treat quantity steppers, a delivery-frequency selector,
 * a live price summary, and submits the real selection to the cart as
 * individual line items (each with its own variant + selling plan) via
 * the storefront cart Ajax API. No box product variant is ever added.
 */
class RsBuildYourBox extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.maxItems = parseInt(this.dataset.maxItems, 10) || 6;
    this.minItems = parseInt(this.dataset.minItems, 10) || 1;
    this.shippingEstimateCents = parseInt(this.dataset.shippingEstimateCents, 10) || 0;
    this.minSubtotalCents = parseInt(this.dataset.minSubtotalCents, 10) || 0;
    this.isHouseAccount = this.dataset.houseAccount === 'true';

    this.#bindStepperButtons();
    this.#bindFrequencyInputs();
    this.#bindSubmit();
    this.#syncState();
  }

  #bindStepperButtons() {
    const buttons = this.querySelectorAll('.rs-byob__stepper-btn');
    for (const btn of buttons) {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const action = btn.dataset.action;
        const targetId = btn.dataset.target;
        if (action && targetId) {
          this.#handleStep(targetId, action);
        }
      });
    }
  }

  #bindFrequencyInputs() {
    const inputs = this.querySelectorAll('input[name="byob-frequency"]');
    for (const input of inputs) {
      input.addEventListener('change', () => {
        for (const other of inputs) {
          other.closest('.rs-byob__frequency-card')?.classList.toggle('rs-byob__frequency-card--selected', other.checked);
        }
        this.#syncState();
      });
    }
  }

  #bindSubmit() {
    const submitBtn = this.refs.submitBtn;
    if (!submitBtn) return;
    submitBtn.addEventListener('click', () => this.#submit());
  }

  #handleStep(targetId, action) {
    const input = this.querySelector(`#${CSS.escape(targetId)}`);
    if (!input) return;

    let current = parseInt(input.value, 10) || 0;
    const total = this.#getTotal();

    if (action === 'increase') {
      if (total >= this.maxItems) return;
      current += 1;
    } else if (action === 'decrease') {
      if (current <= 0) return;
      current -= 1;
    }

    input.value = String(current);
    input.disabled = current < 1;

    const display = this.querySelector(`#${CSS.escape(targetId)}-display`);
    if (display) display.textContent = String(current);

    this.#syncState();
  }

  #getSelectedItems() {
    const items = [];
    const inputs = this.querySelectorAll('[data-protein-input]');
    for (const input of inputs) {
      const qty = parseInt(input.value, 10) || 0;
      if (qty < 1) continue;
      items.push({
        variantId: input.dataset.variantId,
        priceCents: parseInt(input.dataset.priceCents, 10) || 0,
        quantity: qty,
      });
    }
    return items;
  }

  #getTotal() {
    let total = 0;
    for (const item of this.#getSelectedItems()) total += item.quantity;
    return total;
  }

  #getSelectedFrequency() {
    const checked = this.querySelector('input[name="byob-frequency"]:checked');
    if (!checked) return null;
    return {
      planId: checked.value,
      discountPct: parseFloat(checked.dataset.discountPct) || 0,
    };
  }

  #formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  #syncState() {
    const items = this.#getSelectedItems();
    const total = this.#getTotal();
    const isFull = total >= this.maxItems;
    const meetsMin = total >= this.minItems;
    const subtotalCents = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
    const meetsMinSubtotal = subtotalCents >= this.minSubtotalCents;

    const counterText = this.refs.counterText;
    if (counterText) counterText.textContent = `${total} item${total === 1 ? '' : 's'} selected`;

    const counterFill = this.refs.counterFill;
    if (counterFill) {
      const pct = this.maxItems > 0 ? Math.min((total / this.maxItems) * 100, 100) : 0;
      counterFill.style.width = `${pct}%`;
      counterFill.dataset.full = isFull ? 'true' : 'false';
    }

    const itemEls = this.querySelectorAll('.rs-byob__item');
    for (const el of itemEls) {
      const input = el.querySelector('[data-protein-input]');
      const qty = input ? parseInt(input.value, 10) || 0 : 0;
      el.dataset.selected = qty > 0 ? 'true' : 'false';
    }

    const plusButtons = this.querySelectorAll('[data-action="increase"]');
    for (const btn of plusButtons) btn.disabled = isFull;

    const minusButtons = this.querySelectorAll('[data-action="decrease"]');
    for (const btn of minusButtons) {
      const targetId = btn.dataset.target;
      const input = this.querySelector(`#${CSS.escape(targetId)}`);
      const qty = input ? parseInt(input.value, 10) || 0 : 0;
      btn.disabled = qty <= 0;
    }

    const validationMsg = this.refs.validationMsg;
    const validationText = this.refs.validationText;
    if (validationMsg && validationText) {
      if (!meetsMin && total > 0) {
        validationText.textContent = `Please select at least ${this.minItems} item${this.minItems > 1 ? 's' : ''} to continue.`;
        validationMsg.hidden = false;
      } else if (meetsMin && !meetsMinSubtotal && total > 0) {
        validationText.textContent = `Please add ${this.#formatMoney(this.minSubtotalCents - subtotalCents)} more to reach the ${this.#formatMoney(this.minSubtotalCents)} minimum to subscribe.`;
        validationMsg.hidden = false;
      } else {
        validationMsg.hidden = true;
      }
    }

    // Price summary
    const frequency = this.#getSelectedFrequency();
    const discountCents = frequency ? Math.round((subtotalCents * frequency.discountPct) / 100) : 0;
    // House account (#163): the comp is applied by the discount Function at
    // checkout, so show the real payable total here rather than an estimate
    // the customer will never be charged.
    const totalCents = this.isHouseAccount ? 0 : subtotalCents - discountCents + this.shippingEstimateCents;

    if (this.refs.summarySubtotal) this.refs.summarySubtotal.textContent = this.#formatMoney(subtotalCents);
    if (this.refs.summaryDiscountRow) {
      this.refs.summaryDiscountRow.hidden = discountCents <= 0;
      if (this.refs.summaryDiscount) this.refs.summaryDiscount.textContent = `-${this.#formatMoney(discountCents)}`;
      if (this.refs.summaryDiscountLabel && frequency) {
        this.refs.summaryDiscountLabel.textContent = `Subscription discount (${frequency.discountPct}%)`;
      }
    }
    if (this.refs.summaryTotal) this.refs.summaryTotal.textContent = this.#formatMoney(totalCents);

    // Submit button
    const submitBtn = this.refs.submitBtn;
    if (submitBtn) submitBtn.disabled = !meetsMin || !meetsMinSubtotal || items.length === 0;
  }

  async #submit() {
    const items = this.#getSelectedItems();
    const frequency = this.#getSelectedFrequency();
    const submitBtn = this.refs.submitBtn;
    const errorMsg = this.refs.errorMsg;

    if (errorMsg) {
      errorMsg.hidden = true;
      errorMsg.textContent = '';
    }

    const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
    const subtotalCents = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
    const frequencySatisfied = this.isHouseAccount || Boolean(frequency);
    if (items.length === 0 || !frequencySatisfied || totalQty < this.minItems || subtotalCents < this.minSubtotalCents) return;

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding to cart…';
    }

    const payload = {
      items: items.map((item) => {
        const line = {
          id: item.variantId,
          quantity: item.quantity,
        };
        if (!this.isHouseAccount) {
          line.selling_plan = frequency.planId;
        }
        return line;
      }),
    };

    try {
      const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add items to cart.');
      }

      if (this.isHouseAccount) {
        await this.#markHouseAccountOrder();
      }

      window.location.href = window.Theme?.routes?.cart_url || '/cart';
    } catch (err) {
      if (errorMsg) {
        errorMsg.textContent = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
        errorMsg.hidden = false;
      }
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = this.isHouseAccount ? 'Add box to cart' : 'Subscribe to this box';
      }
    }
  }

  async #markHouseAccountOrder() {
    try {
      const updateUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update.js';
      await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'House account': 'Owner comp' } }),
      });
    } catch (err) {
      console.error('rs-build-your-box: failed to mark house account order', err);
    }
  }
}

customElements.define('rs-build-your-box-component', RsBuildYourBox);
